/**
 * Référence des préfixes API alignés sur `apps/backend/src/routes/index.ts`.
 * Utilisé par le panneau MCP institution et le centre d’aide in-app.
 */
export const API_DOC_PATHS = {
  swagger: '/api/docs',
  openapiJson: '/api/openapi.json',
  health: '/api/health',
} as const;

export interface ApiRouteModule {
  prefix: string;
  description: string;
  /** Clé i18n (ex. institution.mcp.routes.v1.auth) pour le panneau MCP. */
  i18nKey: string;
}

/** Préfixes V1 montés sur l’app Express (ordre logique métier). */
export const API_V1_ROUTE_MODULES: readonly ApiRouteModule[] = [
  { prefix: '/api/auth', description: 'Connexion, OTP, profil, sessions, clés API personnelles (/me/api-keys)', i18nKey: 'institution.mcp.routes.v1.auth' },
  { prefix: '/api/projects', description: 'Projets créateur', i18nKey: 'institution.mcp.routes.v1.projects' },
  { prefix: '/api/admin', description: 'Administration plateforme', i18nKey: 'institution.mcp.routes.v1.admin' },
  { prefix: '/api/ai', description: 'Fonctions IA', i18nKey: 'institution.mcp.routes.v1.ai' },
  { prefix: '/api/audio', description: 'Audio & TTS studio', i18nKey: 'institution.mcp.routes.v1.audio' },
  { prefix: '/api/video', description: 'Vidéo', i18nKey: 'institution.mcp.routes.v1.video' },
  { prefix: '/api/pdf', description: 'PDF', i18nKey: 'institution.mcp.routes.v1.pdf' },
  { prefix: '/api/pptx', description: 'PowerPoint', i18nKey: 'institution.mcp.routes.v1.pptx' },
  { prefix: '/api/eduflows', description: 'Parcours EduFlow (blocs, publication, partage, versions…)', i18nKey: 'institution.mcp.routes.v1.eduflows' },
  { prefix: '/api/eduflow-progress', description: 'Progression EduFlow', i18nKey: 'institution.mcp.routes.v1.eduflowProgress' },
  { prefix: '/api/participants', description: 'Participants / apprenants (côté concepteur)', i18nKey: 'institution.mcp.routes.v1.participants' },
  { prefix: '/api/store', description: 'Boutique & audiobooks', i18nKey: 'institution.mcp.routes.v1.store' },
  { prefix: '/api/stripe', description: 'Paiements Stripe', i18nKey: 'institution.mcp.routes.v1.stripe' },
  { prefix: '/api/institutions', description: 'Institutions (routeur principal)', i18nKey: 'institution.mcp.routes.v1.institutions' },
  { prefix: '/api/institution/content', description: 'Contenu institution', i18nKey: 'institution.mcp.routes.v1.institutionContent' },
  { prefix: '/api/institution/rh-dashboard', description: 'Tableau de bord RH', i18nKey: 'institution.mcp.routes.v1.institutionRhDashboard' },
  { prefix: '/api/institution/training-sessions', description: 'Sessions de formation', i18nKey: 'institution.mcp.routes.v1.institutionTrainingSessions' },
  { prefix: '/api/institution/analytics/bi', description: 'Analytics / BI', i18nKey: 'institution.mcp.routes.v1.institutionAnalyticsBi' },
  { prefix: '/api/institution', description: 'Dashboard institution (dont clés API MCP : /api-keys)', i18nKey: 'institution.mcp.routes.v1.institutionDashboard' },
  { prefix: '/api/webhooks', description: 'Webhooks', i18nKey: 'institution.mcp.routes.v1.webhooks' },
  { prefix: '/api/support', description: 'Support', i18nKey: 'institution.mcp.routes.v1.support' },
  { prefix: '/api/analytics', description: 'Analytics', i18nKey: 'institution.mcp.routes.v1.analytics' },
  { prefix: '/api/dashboard', description: 'Dashboard créateur', i18nKey: 'institution.mcp.routes.v1.dashboard' },
  { prefix: '/api/public', description: 'Pages publiques', i18nKey: 'institution.mcp.routes.v1.public' },
  { prefix: '/api/podcasts', description: 'Podcasts (+ partage apprenant /learner/share)', i18nKey: 'institution.mcp.routes.v1.podcasts' },
  { prefix: '/api/webinars', description: 'Webinaires', i18nKey: 'institution.mcp.routes.v1.webinars' },
  { prefix: '/api/images', description: 'Images', i18nKey: 'institution.mcp.routes.v1.images' },
  { prefix: '/api/user-sounds', description: 'Sons utilisateur', i18nKey: 'institution.mcp.routes.v1.userSounds' },
  { prefix: '/api/tts', description: 'Synthèse vocale', i18nKey: 'institution.mcp.routes.v1.tts' },
  { prefix: '/api/ai-generation', description: 'Génération IA EduFlow', i18nKey: 'institution.mcp.routes.v1.aiGeneration' },
  { prefix: '/api/pdf-extractor', description: 'Extraction PDF', i18nKey: 'institution.mcp.routes.v1.pdfExtractor' },
];

/** Sous-routeurs V2 (tous sous /api/v2, avec auth + institution + audit). */
export const API_V2_ROUTE_MODULES: readonly ApiRouteModule[] = [
  { prefix: '/api/v2/campaigns', description: 'Campagnes', i18nKey: 'institution.mcp.routes.v2.campaigns' },
  { prefix: '/api/v2/competencies', description: 'Compétences', i18nKey: 'institution.mcp.routes.v2.competencies' },
  { prefix: '/api/v2/compliance', description: 'Conformité', i18nKey: 'institution.mcp.routes.v2.compliance' },
  { prefix: '/api/v2/reporting', description: 'Reporting', i18nKey: 'institution.mcp.routes.v2.reporting' },
  { prefix: '/api/v2/workflows', description: 'Workflows', i18nKey: 'institution.mcp.routes.v2.workflows' },
  { prefix: '/api/v2/catalog', description: 'Catalogue', i18nKey: 'institution.mcp.routes.v2.catalog' },
  { prefix: '/api/v2/integrations', description: 'Intégrations', i18nKey: 'institution.mcp.routes.v2.integrations' },
  { prefix: '/api/v2/gdpr', description: 'RGPD', i18nKey: 'institution.mcp.routes.v2.gdpr' },
  { prefix: '/api/v2/manager', description: 'Espace manager', i18nKey: 'institution.mcp.routes.v2.manager' },
];

export interface ApiExtraRoute {
  method: string;
  path: string;
  note: string;
  i18nKey: string;
}

export const API_EXTRA_ROUTES: readonly ApiExtraRoute[] = [
  { method: 'POST', path: '/api/eduflows/:id/designer/ai-chat', note: 'Assistant IA concepteur (chat)', i18nKey: 'institution.mcp.extra.eduflowAiChat' },
  { method: 'POST', path: '/api/eduflows/:id/designer/ai-document', note: 'Contexte document (upload)', i18nKey: 'institution.mcp.extra.eduflowAiDocument' },
  { method: 'GET', path: '/api/me/groups', note: 'Groupes de l’institution courante', i18nKey: 'institution.mcp.extra.meGroups' },
  { method: 'POST', path: '/api/stripe/webhook', note: 'Webhook Stripe (corps brut JSON — défini dans app.ts)', i18nKey: 'institution.mcp.extra.stripeWebhook' },
  { method: 'GET', path: '/api/podcasts/learner/share', note: 'Lecture partagée apprenant (podcast)', i18nKey: 'institution.mcp.extra.podcastLearnerShare' },
  { method: 'GET', path: '/api/health', note: 'Santé du service', i18nKey: 'institution.mcp.extra.health' },
];

export const MCP_HTTP_ENDPOINTS = {
  /** Clés API gérées par l’admin institution (dashboard). */
  institutionKeys: {
    list: 'GET /api/institution/api-keys',
    create: 'POST /api/institution/api-keys',
    updateName: 'PATCH /api/institution/api-keys/:id',
    toggle: 'PATCH /api/institution/api-keys/:id/toggle',
    remove: 'DELETE /api/institution/api-keys/:id',
  },
  /** Clés personnelles concepteur (paramètres compte). */
  userKeys: {
    list: 'GET /api/auth/me/api-keys',
    create: 'POST /api/auth/me/api-keys',
    toggle: 'PATCH /api/auth/me/api-keys/:id/toggle',
    remove: 'DELETE /api/auth/me/api-keys/:id',
  },
} as const;

export function absoluteUrl(path: string): string {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path.startsWith('/') ? path : `/${path}`}`;
}
